'use client'

import React, { useState, useEffect, useRef } from 'react';
import {FilePreview} from "@/components/Form/types";

// Props for the FileDropOverlay component
interface FileDropOverlayProps {
    onDrop: (files: FilePreview[]) => void;
    accept?: string;
}

const FileDropOverlay: React.FC<FileDropOverlayProps> = ({ onDrop, accept }) => {
    const [isDragging, setIsDragging] = useState(false);
    const overlayRef = useRef<HTMLDivElement>(null);

    let dragLeaveTimer: NodeJS.Timeout | null = null;
    let lastMousePosition = { x: 0, y: 0 };

    const isFileAccepted = (file: File) => {
        const acceptFormat = accept || '*/*';
        const acceptedTypes = acceptFormat.split(',').map(type => type.trim());

        return acceptedTypes.some(type => {

            if (file.type) {
                if (type.endsWith('/*')) {
                    return file.type.startsWith(type.slice(0, -2));
                }
                if (type.indexOf('/')) {
                    return file.type === type;
                }
            }
            if (type.startsWith('.')) {
                return file.name.toLowerCase().endsWith(type.toLowerCase());
            }
            return file.type === type;
        });
    }

    // Utility function to clear the drag leave timer
    const clearDragTimer = () => {
        if (dragLeaveTimer) {
            clearTimeout(dragLeaveTimer);
        }
    };

    useEffect(() => {
        // Event handler: Window drag enter
        const handleWindowDragEnter = (event: DragEvent) => {
	        const reservedDragging = (window as any).reservedDragging || false;
	        if (!reservedDragging) {
		        event.preventDefault();
		        clearDragTimer();
		        setIsDragging(true);
	        }
        };

        // Event handler: Window drag over
        const handleWindowDragOver = (event: DragEvent) => {
	        const reservedDragging = (window as any).reservedDragging || false;
	        if (!reservedDragging) {
                event.preventDefault();
                clearDragTimer();
			}
        };

        // Event handler: Window drag leave
        const handleWindowDragLeave = (event: DragEvent) => {
            event.preventDefault();
            clearDragTimer();
            dragLeaveTimer = setTimeout(() => {
                setIsDragging(false);
            }, 50);
        };

        // Event handler: Window drop
        const handleWindowDrop = (event: DragEvent) => {
            event.preventDefault();
            setIsDragging(false);
            clearDragTimer();
        };

        // Event handler: Mouse move
        const handleMouseMove = (event: MouseEvent) => {
            lastMousePosition = { x: event.clientX, y: event.clientY };
        };

        // Event handler: Window blur (window loses focus)
        const handleWindowBlur = () => {
            setIsDragging(false);
            clearDragTimer();
        };

        // Adding all necessary event listeners
        window.addEventListener('dragenter', handleWindowDragEnter);
        window.addEventListener('dragover', handleWindowDragOver);
        window.addEventListener('dragleave', handleWindowDragLeave);
        window.addEventListener('drop', handleWindowDrop);
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('blur', handleWindowBlur);

        // Cleanup: Remove all event listeners when component unmounts
        return () => {
            window.removeEventListener('dragenter', handleWindowDragEnter);
            window.removeEventListener('dragover', handleWindowDragOver);
            window.removeEventListener('dragleave', handleWindowDragLeave);
            window.removeEventListener('drop', handleWindowDrop);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('blur', handleWindowBlur);
            clearDragTimer();
        };
    }, []);

    // Handle drop event on the overlay
    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
	    const el = event.target as HTMLElement;
	    if (el.classList.contains("DraggableSortListItem")) return

	    event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        clearDragTimer();

        const files = Array.from(event.dataTransfer.files || []);
        const acceptedFiles = files.filter(isFileAccepted);

        const previews: FilePreview[] = await Promise.all(
            acceptedFiles.map(async (file) => {
                const string = await readFileAsDataURL(file);
                const ext = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : '';
                return {
                    data: file,
                    string,
                    name: file.name.length > 50 ? file.name.slice(0, 50) + '...' : file.name,
                    size: file.size,
                    type: file.type,
                    url: undefined, // or remove if not in your type
                    fileExtension: ext
                } as FilePreview;
            })
        );

        if (previews.length) onDrop(previews);
        event.dataTransfer.clearData();
    };

    return (
        <div
            ref={overlayRef}
            className={`fixed inset-0 ${isDragging ? 'bg-primary-500 bg-opacity-90 pointer-events-auto' : 'pointer-events-none'} flex items-center justify-center z-50`}
            onDrop={handleDrop}
        >
            {isDragging && (
                <div className="text-title-xxl text-white text-shadow-sm">
                    Drop your file to start the upload process.
                </div>
            )}
        </div>
    );
};


// helper
const readFileAsDataURL = (file: File) =>
    new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = reject;
        r.readAsDataURL(file);
    });




export default FileDropOverlay;
